import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { useAuthStore } from '../store/authStore';
import { hasAtLeastPermission } from '../utils/moduleAccess';
import { authAPI, companiesAPI, devicesAPI } from '../services/api';
import DataTable from '../components/DataTable';
import '../App.css';
import { formatDateForTimezone } from '../utils/datetime';

const Devices = () => {
  const [devices, setDevices] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [bulkUploadCompanyId, setBulkUploadCompanyId] = useState(null);
  
  // for SSE progress tracking
  const [progressId, setProgressId] = useState(null);
  const [progressData, setProgressData] = useState(null);
  const [showProgress, setShowProgress] = useState(false);
  
  const [formData, setFormData] = useState({
    uid: '',
    box: '',
    bag: '',
    seal: '',
    password1: '',
    password2: '',
    password3: '',
    password4: '',
    container_number: '',
    company_id: null,
  });

  const { user, setUser } = useAuthStore();
  const timezone = useAuthStore((state) => state.timezone);
  const permissions = useAuthStore((state) => state.permissions);
  const isAdminLike = user?.user_type === 'gods_eye';
  const canEdit = isAdminLike || hasAtLeastPermission(permissions, 'devices', 'edit');
  const isReadOnly = !canEdit || user?.user_type === 'manager';

  useEffect(() => {
    const restoreUser = async () => {
      if (!user && localStorage.getItem('token')) {
        try {
          const response = await authAPI.getCurrentUser();
          setUser(response.data);
        } catch (err) {
          console.error('Failed to restore user:', err);
        }
      }
    };
    
    restoreUser();
  }, [user, setUser]);

  // Monitor progress stream for bulk uploads
  useEffect(() => {
    if (!progressId) return;

    // Use the same /api base path as the axios client, avoiding double /api prefixes
    const rawBase = process.env.REACT_APP_API_URL || '/api';
    const normalizedBase = rawBase.replace(/\/+$/, '');
    const eventSource = new EventSource(`${normalizedBase}/devices/progress/${progressId}`);

    eventSource.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        setProgressData(data);
        
        if (data.status === 'complete') {
          eventSource.close();
          setProgressId(null);
        }
      } catch (error) {
        console.error('Error parsing progress data:', error);
      }
    };

    eventSource.onerror = (error) => {
      console.error('Progress stream error:', error);
      eventSource.close();
      setProgressId(null);
    };

    return () => eventSource.close();
  }, [progressId]);

  useEffect(() => {
    fetchDevices();
    fetchCompanies();
  }, []);

  const fetchCompanies = async () => {
    try {
      const response = await companiesAPI.getAll();
      setCompanies(response.data);
      // Auto-select user's company for bulk upload if they're not gods_eye
      if (user?.user_type !== 'gods_eye' && user?.company_id) {
        setBulkUploadCompanyId(user.company_id);
      }
    } catch (err) {
      console.error('Failed to fetch companies:', err);
      toast.error('Failed to fetch companies');
    }
  };

  const fetchDevices = async () => {
    try {
      setLoading(true);
      const response = await devicesAPI.getAll();
      setDevices(response.data);
    } catch (err) {
      toast.error('Failed to fetch devices');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ 
      ...formData, 
      [name]: name === 'company_id' ? (value ? parseInt(value) : null) : value 
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Managers are explicitly not allowed to create devices
    if (!canEdit || user?.user_type === 'manager') {
      toast.error('You do not have permission to create or edit seals');
      return;
    }

    try {
      if (editingId) {
        await devicesAPI.update(editingId, formData);
        toast.success('Seal updated successfully');
      } else {
        await devicesAPI.create(formData);
        toast.success('Seal created successfully');
      }

      setFormData({
        uid: '',
        box: '',
        bag: '',
        seal: '',
        password1: '',
        password2: '',
        password3: '',
        password4: '',
        container_number: '',
        company_id: null,
      });
      setEditingId(null);
      setShowForm(false);
      fetchDevices();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save seal');
    }
  };

  const handleEdit = (device) => {
    // Managers are explicitly not allowed to edit devices
    if (!canEdit || user?.user_type === 'manager') {
      toast.error('You do not have permission to edit seals');
      return;
    }
    setFormData({
      uid: device.uid,
      box: device.box,
      bag: device.bag || '',
      seal: device.seal,
      password1: device.password1 || '',
      password2: device.password2 || '',
      password3: device.password3 || '',
      password4: device.password4 || '',
      container_number: device.container_number || '',
      company_id: device.company_id || null,
    });
    setEditingId(device.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    // Managers are explicitly not allowed to delete devices
    if (!canEdit || user?.user_type === 'manager') {
      toast.error('You do not have permission to delete seals');
      return;
    }

    if (window.confirm('Are you sure you want to delete this seal?')) {
      try {
        await devicesAPI.delete(id);
        toast.success('Seal deleted successfully');
        fetchDevices();
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Failed to delete seal');
      }
    }
  };

  const handleAddClick = () => {
    // Auto-select user's company for non-gods_eye users
    const defaultCompanyId = user?.user_type !== 'gods_eye' ? user?.company_id : null;
    setFormData({
      uid: '',
      box: '',
      bag: '',
      seal: '',
      password1: '',
      password2: '',
      password3: '',
      password4: '',
      container_number: '',
      company_id: defaultCompanyId,
    });
    setEditingId(null);
    setShowForm(true);
  };

  const handleExcelUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

      // Managers are explicitly not allowed to upload seals
      if (!canEdit || user?.user_type === 'manager') {
        toast.error('You do not have permission to upload seals');
      return;
    }

    if (!bulkUploadCompanyId) {
      toast.error('Please select a company for bulk upload');
      return;
    }

    if (!file.name.match(/\.(xlsx|xls|csv)$/i)) {
      toast.error('Please upload a valid file (.xlsx, .xls, or .csv)');
      return;
    }

    try {
      setUploading(true);
      setShowProgress(true);
      const formDataMultipart = new FormData();
      formDataMultipart.append('file', file);
      formDataMultipart.append('company_id', String(bulkUploadCompanyId));

      const response = await devicesAPI.bulkUpload(formDataMultipart, (progressEvent) => {
        const percentCompleted = Math.round(
          (progressEvent.loaded * 100) / progressEvent.total
        );
        setUploadProgress(percentCompleted);
      });
      
      // Get operation_id for SSE progress tracking
      if (response.data?.operation_id) {
        setProgressId(response.data.operation_id);
      }


      // Check if response is valid
      if (response.status === 201 && response.data) {
        const responseData = response.data;
        const deviceCount = Array.isArray(responseData.created_devices) ? responseData.created_devices.length : 0;
        const rowErrors = Array.isArray(responseData.errors) ? responseData.errors : [];
        
        if (rowErrors.length === 0 && deviceCount > 0) {
          toast.success(`? Successfully uploaded ${deviceCount} seals`);
        } else if (rowErrors.length === 0) {
          toast.success('? Seals uploaded successfully');
        }
        
        // Show any row-level errors if they exist
        if (rowErrors.length > 0) {
          toast.error(`Uploaded ${deviceCount} seals; ${rowErrors.length} row${rowErrors.length !== 1 ? 's were' : ' was'} skipped`);
          // Convert error objects to readable strings
          const errorMsg = rowErrors.slice(0, 5).map(err => {
            if (typeof err === 'string') return err;
            if (err.msg && err.loc) return `${err.loc.join('.')}: ${err.msg}`;
            if (err.row && err.error) return `Row ${err.row}: ${err.error}`;
            // Fallback: stringify
            return JSON.stringify(err);
          }).join('\n');
          toast.error(`Skipped rows:\n${errorMsg}${rowErrors.length > 5 ? '\n...' : ''}`);
        }
      }
      
      fetchDevices();
      if (e.target) e.target.value = '';
    } catch (err) {
      console.error('Upload error details:', err);
      
      let errorMessage = 'Failed to upload file';
      
      // Handle network/response errors
      if (err.response) {
        const status = err.response.status;
        const data = err.response.data;
        
        // Handle 422 or 400 validation/request errors
        if (status === 422 || status === 400) {
          // Check if response is an array of validation errors (Pydantic format)
          if (Array.isArray(data)) {
            const validationErrors = data
              .slice(0, 3)
              .map((e) => {
                if (e.loc && e.msg) {
                  return `${e.loc.join('.')}: ${e.msg}`;
                }
                // Fallback: stringify
                return JSON.stringify(e);
              })
              .join('\n');
            errorMessage = `Validation error:\n${validationErrors}${data.length > 3 ? '\n...' : ''}`;
          }
          // Check if response is an object with detail property
          else if (data?.detail) {
            errorMessage = data.detail;
          }
          // Check if it's our custom error format
          else if (data?.errors && Array.isArray(data.errors)) {
            const errorMsg = data.errors.slice(0, 3).map(err => {
              if (typeof err === 'string') return err;
              if (err.msg && err.loc) return `${err.loc.join('.')}: ${err.msg}`;
              if (err.row && err.error) return `Row ${err.row}: ${err.error}`;
              return JSON.stringify(err);
            }).join('\n');
            errorMessage = `Upload failed:\n${errorMsg}${data.errors.length > 3 ? '\n...' : ''}`;
          }
          // Unknown format - just show raw error
          else {
            errorMessage = 'File validation failed. Please check the format.';
          }
        } 
        // Handle 403 permission error
        else if (status === 403) {
          errorMessage = data?.detail || 'You do not have permission to upload files';
        }
        // Handle other HTTP errors
        else {
          errorMessage = data?.detail || data?.message || `Error: ${status}`;
        }
      } 
      // Handle network errors
      else if (err.request) {
        errorMessage = 'Network error - please check your connection';
      }
      
      toast.error(errorMessage);
    } finally {
      setUploading(false);
      setUploadProgress(0);
      setShowProgress(false);
    }
  };

  const columns = [
    { key: 'id', label: 'ID', width: '80px', sortable: true },
    { key: 'box', label: 'Box', sortable: true },
    { key: 'bag', label: 'Bag', sortable: true },
    // Hide seal for operador, show for gods_eye and cliente
    ...(user?.user_type !== 'operador' ? [
      { key: 'seal', label: 'Seal', sortable: true }
    ] : []),
    { key: 'container_number', label: 'Container #', sortable: true },
    {
      key: 'container_assigned_by',
      label: 'Assigned By',
      render: (value, row) => value?.username || '-',
      sortable: false
    },
    {
      key: 'container_assigned_at',
      label: 'Assigned At',
      render: (value) =>
        value
          ? formatDateForTimezone(value, timezone, 'es-ES', {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
            })
          : '-',
      sortable: false
    },
    ...(user?.user_type === 'gods_eye' ? [
      { key: 'uid', label: 'UID', sortable: true }
    ] : []),
    {
      key: 'company_id',
      label: 'Company',
      render: (value, row) => row.company?.name || 'Unassigned',
      sortable: true
    },
  ];

  return (
    <div className="container">
      <h1 className="page-title">Seals Management</h1>

      {isReadOnly && (
        <div style={{
          backgroundColor: '#fff3cd',
          border: '1px solid #ffc107',
          color: '#856404',
          padding: '12px',
          borderRadius: '4px',
          marginBottom: '20px',
        }}>
          You have read-only access
        </div>
      )}

      {canEdit && user?.user_type !== 'manager' && (
        <>
          <button className="btn btn-primary" onClick={handleAddClick} style={{ marginBottom: '20px' }}>
            + Add Seal
          </button>

          <div style={{ marginBottom: '30px', padding: '16px', backgroundColor: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px' }}>
            <h3>Bulk Upload Seals</h3>
            <p style={{ color: '#666', fontSize: '14px', marginBottom: '15px' }}>
              Upload .xlsx, .xls, or .csv files. <a href="/device-upload-template.xlsx" download style={{ color: '#3b82f6', textDecoration: 'none' }}>Download template</a>
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'flex-end' }}>
              <div style={{ flex: '1 1 220px', minWidth: '220px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                  Select Company for Upload *
                </label>
                <select
                  value={bulkUploadCompanyId || ''}
                  onChange={(e) => setBulkUploadCompanyId(e.target.value ? parseInt(e.target.value) : null)}
                  disabled={user?.user_type !== 'gods_eye'}
                  style={{ 
                    width: '100%', 
                    padding: '10px', 
                    border: '1px solid #e5e7eb', 
                    borderRadius: '4px',
                    backgroundColor: '#fff'
                  }}
                  required
                >
                  <option value="">-- Select Company --</option>
                  {companies.map(company => (
                    <option key={company.id} value={company.id}>
                      {company.name}
                    </option>
                  ))}
                </select>
              </div>
              <div style={{ flex: '1 1 260px', minWidth: '260px', display: 'flex', gap: '10px', alignItems: 'center' }}>
                <input 
                  type="file" 
                  accept=".xlsx,.xls,.csv" 
                  onChange={handleExcelUpload} 
                  disabled={uploading}
                  style={{ padding: '10px', border: '1px solid #e5e7eb', borderRadius: '4px', flex: 1 }} 
                />
                {uploading && (
                  <div style={{ minWidth: '150px' }}>
                    <div style={{ width: '100%', height: '20px', backgroundColor: '#e9ecef', borderRadius: '4px', overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: `${uploadProgress}%`, backgroundColor: '#16a34a', transition: 'width 0.3s ease' }}></div>
                    </div>
                    <p style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>{uploadProgress}%</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}

      {showForm && (
        <div className="modal active">
          <div className="modal-content">
            <span className="close" onClick={() => { 
              setShowForm(false); 
              setEditingId(null);
              setFormData({
                uid: '',
                box: '',
                seal: '',
                password1: '',
                password2: '',
                password3: '',                container_number: '',                password4: '',
                company_id: null,
              });
            }}>&times;</span>
            <h2>{editingId ? 'Edit Seal' : 'Add Seal'}</h2>
            
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>UID</label>
                <input
                  type="text"
                  name="uid"
                  value={formData.uid}
                  onChange={handleInputChange}
                  required
                  disabled={editingId !== null}
                />
              </div>
              <div className="form-group">
                <label>Box</label>
                <input
                  type="text"
                  name="box"
                  value={formData.box}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>Bag</label>
                <input
                  type="text"
                  name="bag"
                  value={formData.bag}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>Seal</label>
                <input
                  type="text"
                  name="seal"
                  value={formData.seal}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>Password 1</label>
                <input
                  type="text"
                  name="password1"
                  value={formData.password1}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>Password 2</label>
                <input
                  type="text"
                  name="password2"
                  value={formData.password2}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>Password 3</label>
                <input
                  type="text"
                  name="password3"
                  value={formData.password3}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>Password 4</label>
                <input
                  type="text"
                  name="password4"
                  value={formData.password4}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>Container Number</label>
                <input
                  type="text"
                  name="container_number"
                  value={formData.container_number}
                  onChange={handleInputChange}
                  placeholder="Optional - e.g., CONTAINER_001"
                />
              </div>
              <div className="form-group">
                <label>Company</label>
                <select
                  name="company_id"
                  value={formData.company_id || ''}
                  onChange={handleInputChange}
                  disabled={user?.user_type !== 'gods_eye'}
                  required
                >
                  <option value="">-- Select Company --</option>
                  {companies.map(company => (
                    <option key={company.id} value={company.id}>
                      {company.name}
                    </option>
                  ))}
                </select>
              </div>
              <button type="submit" className="btn btn-success">
                {editingId ? 'Update' : 'Create'}
              </button>
            </form>
          </div>
        </div>
      )}

      {loading ? (
        <p>Loading...</p>
      ) : (
        canEdit ? (
          <DataTable
            data={devices}
            columns={columns}
            searchableFields={['uid', 'box', 'bag', 'seal']}
            itemsPerPage={10}
            onEdit={handleEdit}
            onDelete={(device) => handleDelete(device.id)}
            title="Seals List"
          />
        ) : (
          <DataTable
            data={devices}
            columns={columns.filter(col => col.key !== 'actions')}
            searchableFields={['uid', 'box', 'bag', 'seal']}
            itemsPerPage={10}
            title="Seals List"
          />
        )
      )}

      {/* Progress Bar for Bulk Upload */}
      {showProgress && progressData && (
        <div className="progress-container">
          <div className="progress-header">
            <h4>Upload Progress</h4>
            <span className="progress-status">{progressData.status === 'complete' ? '? Complete' : 'In Progress...'}</span>
          </div>
          <div className="progress-info">
            <span>{progressData.current} / {progressData.total} seals</span>
            <span className="progress-percentage">{progressData.percentage}%</span>
          </div>
          <div className="progress-bar">
            <div 
              className="progress-fill" 
              style={{ 
                width: `${progressData.percentage}%`,
                backgroundColor: progressData.status === 'error' ? '#dc3545' : '#28a745'
              }}
            ></div>
          </div>
          {progressData.message && (
            <div className="progress-message">
              {progressData.message}
            </div>
          )}
        </div>
      )}

      <div style={{ marginTop: '30px', padding: '15px', backgroundColor: '#e7f3ff', border: '1px solid #b3d9ff', borderRadius: '4px', fontSize: '12px', color: '#004085' }}>
        <strong>Role:</strong> {user?.user_type || 'Unknown'} | <strong>Company:</strong> {user?.company_id || 'Unknown'}
      </div>
    </div>
  );
};

export default Devices;
